import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import { Spinner } from 'react-bootstrap'
import { FbModal as Modal } from 'components/UI/Modal'
import Select from 'components/FormUtils/Select'
import { SingleVariantPicker } from 'components/UI/SingleVariantPicker'
import type { SelectedVariant } from 'components/UI/ProductSearchModal'
import apiService from 'services/api'

const editProductSchema = z.object({
  product_id: z.number({ required_error: 'Product is required' }),
  product_variant_id: z.number().optional().nullable(),
})
type EditProductFormValues = z.infer<typeof editProductSchema>

interface EditDealProductModalProps {
  show: boolean
  onClose: () => void
  dealId: number
  currentProductId: number | null
  currentProductVariantId: number | null
  onSaved: () => void
}

/**
 * Focused modal for changing which product (and variant) a deal is assigned to.
 * Reuses the product dropdown + SingleVariantPicker from the full EditDealModal.
 */
export function EditDealProductModal({
  show,
  onClose,
  dealId,
  currentProductId,
  currentProductVariantId,
  onSaved,
}: EditDealProductModalProps) {
  const api = apiService()

  const { data: productOptions, isLoading: isProductOptionsLoading } =
    api.useGetProductsOptions()
  const { data: products, isLoading: isProductsLoading } = api.useGetProducts()

  const isReady = !isProductOptionsLoading && !isProductsLoading

  const initialSelectedVariant = useMemo<SelectedVariant | null>(() => {
    if (!products || !currentProductVariantId) return null
    const product = products.find((p) => p.id === currentProductId)
    if (!product?.variants) return null
    const variant = product.variants.find(
      (v) => v.id === currentProductVariantId
    )
    if (!variant) return null
    return {
      id: variant.id!,
      name: variant.name,
      format_pack_size: variant.format_pack_size || null,
      sku: variant.sku || '',
      gtin: variant.gtin || null,
      product_id: product.id,
      product_name: product.name,
      distributor_codes: variant.distributor_codes || [],
      avg_price: variant.avg_price || null,
    }
  }, [products, currentProductId, currentProductVariantId])

  if (!show) return null

  if (!isReady) {
    return (
      <Modal
        open={true}
        onOpenChange={(open) => !open && onClose()}
        title="Edit Product"
        onCancel={onClose}
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-2">
            <Spinner animation="border" />
            <span className="text-fg-secondary text-sm">
              Loading products...
            </span>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <EditProductFormContent
      key={dealId}
      dealId={dealId}
      currentProductId={currentProductId}
      initialSelectedVariant={initialSelectedVariant}
      productOptions={productOptions}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

interface EditProductFormContentProps {
  dealId: number
  currentProductId: number | null
  initialSelectedVariant: SelectedVariant | null
  productOptions: Array<{ label: string; value: number }> | undefined
  onClose: () => void
  onSaved: () => void
}

function EditProductFormContent({
  dealId,
  currentProductId,
  initialSelectedVariant,
  productOptions,
  onClose,
  onSaved,
}: EditProductFormContentProps) {
  const api = apiService()

  const [selectedVariant, setSelectedVariant] =
    useState<SelectedVariant | null>(initialSelectedVariant)

  const form = useForm<EditProductFormValues>({
    resolver: zodResolver(editProductSchema),
    mode: 'onChange',
    defaultValues: {
      product_id: currentProductId ?? undefined,
      product_variant_id: initialSelectedVariant?.id ?? null,
    },
  })

  const {
    formState: { isValid, isSubmitting },
    setValue,
    watch,
  } = form

  // Clear variant when product changes
  const watchedProductId = watch('product_id')
  useEffect(() => {
    if (watchedProductId !== currentProductId) {
      setValue('product_variant_id', null, { shouldValidate: true })
      setSelectedVariant(null)
    }
  }, [watchedProductId, currentProductId, setValue])

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await api.editDeal(dealId, {
        product_id: values.product_id,
        product_variant_id: values.product_variant_id,
      })
      toast.success('Product updated')
      onClose()
      onSaved()
    } catch {
      toast.error('Failed to update product')
    }
  })

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      title="Edit Product"
      onAccept={onSubmit}
      onCancel={onClose}
      acceptButtonText="Save"
      loading={isSubmitting}
      acceptButtonProps={{ disabled: !isValid }}
    >
      <div className="space-y-6">
        <Select
          label="Product"
          control={form.control}
          name="product_id"
          options={productOptions}
          placeholder="Select..."
          isClearable={false}
        />

        <div>
          <label className="block text-sm font-medium text-fg-secondary mb-2">
            Product Variant
            <span className="text-fg-secondary ml-1">(Optional)</span>
          </label>
          <SingleVariantPicker
            productId={watchedProductId || null}
            selectedVariant={selectedVariant}
            onVariantSelect={(variant) => {
              if (variant) {
                setValue('product_variant_id', variant.id, {
                  shouldValidate: true,
                })
                setSelectedVariant(variant)
              } else {
                setValue('product_variant_id', null, { shouldValidate: true })
                setSelectedVariant(null)
              }
            }}
            buttonText="Select Variant"
            disabled={!watchedProductId}
          />
        </div>
      </div>
    </Modal>
  )
}
